/**
 * Admin Automation Settings (global delivery policy) Hooks
 *
 * React Query hooks for the platform-wide AutomationSettings singleton:
 * rolling frequency caps (last 7 / 10 / 30 days, 0 = disabled) and quiet
 * hours (recipient-local). GET returns the model defaults with
 * `is_default: true` until something was saved; POST always writes a new row
 * (latest wins) and returns it, which we put straight into the cache.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminAutomationSettingsGet, adminAutomationSettingsUpdate } from "../utils/adminApi";

export const ADMIN_AUTOMATION_SETTINGS_QUERY_KEY = ["admin", "automations", "settings"];

export const useAdminAutomationSettings = ({ enabled = true } = {}) => {
  const query = useQuery({
    queryKey: ADMIN_AUTOMATION_SETTINGS_QUERY_KEY,
    queryFn: () => adminAutomationSettingsGet(),
    enabled,
    refetchOnWindowFocus: false,
  });
  return { ...query, settings: query.data?.settings || null };
};

export const useUpdateAdminAutomationSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminAutomationSettingsUpdate(payload),
    onSuccess: async (data) => {
      if (data?.settings) {
        queryClient.setQueryData(ADMIN_AUTOMATION_SETTINGS_QUERY_KEY, data);
      }
      await queryClient.invalidateQueries({ queryKey: ADMIN_AUTOMATION_SETTINGS_QUERY_KEY });
    },
  });
};
